import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { HERO_STATS } from "./data";
import { Container, MaskText, LimeButton, ArrowDot, scrollToId, ease } from "./primitives";

const Portrait = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <div className="pointer-events-none absolute -inset-10 rounded-full bg-[#1238CE]/40 blur-[100px]" />
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.5, ease, delay: 0.35 }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] bg-[#0C142A] [transform-style:preserve-3d]"
        data-testid="hero-portrait"
      >
        <motion.img
          src="/img/maicon.jpg" alt="Maicon Roberto palestrando sobre estratégia, marketing e vendas"
          style={{ y: imgY, scale: 1.12 }}
          className="absolute inset-0 h-full w-full object-cover object-[50%_0%]"
          data-testid="hero-portrait-img"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-[#060B19]/5 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between sm:bottom-7 sm:left-7 sm:right-7">
          <div>
            <div className="font-display text-xl font-bold text-white">Maicon Roberto</div>
            <div className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-300">Estratégia · Marketing · Vendas</div>
          </div>
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-white/25 bg-white/10 px-3 py-1.5 font-mono-x text-[11px] text-white backdrop-blur-md"><span className="h-1.5 w-1.5 rounded-full bg-[#CCFF00]" />25+ anos</div>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="grain relative overflow-hidden bg-[#060B19] pt-[110px] text-white" data-testid="hero-section">
    <div className="grid-lines pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" />
    <Container className="relative grid items-center gap-14 pb-16 lg:grid-cols-12 lg:gap-10 lg:pb-24">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}
          className="font-mono-x mb-8 inline-flex items-center gap-3 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-slate-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#CCFF00]" /> Consultoria em estratégia, marketing e vendas
        </motion.div>
        <MaskText
          as="h1" onLoad delay={0.4}
          className="font-display text-[44px] font-extrabold leading-[0.95] tracking-[-0.035em] sm:text-6xl lg:text-[84px] xl:text-[96px]"
          lines={["Empresas", "crescem com", <span className="text-[#CCFF00]">direção.</span>]}
          data-testid="hero-title"
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: 1 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
          Conecto estratégia, marketing e vendas para que sua empresa cresça com método, previsibilidade e resultados que se sustentam.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-5">
          <LimeButton onClick={() => scrollToId("contato")} data-testid="hero-primary-cta">
            Quero crescer com direção <ArrowDot />
          </LimeButton>
          <button onClick={() => scrollToId("metodo")} data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-white">
            <span className="border-b border-white/30 pb-0.5 transition-colors group-hover:border-[#CCFF00]">Conhecer o método</span>
          </button>
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <Portrait />
      </div>
    </Container>
    <div className="relative border-t border-white/10">
      <Container className="grid grid-cols-2 lg:grid-cols-4">
        {HERO_STATS.map((s, i) => (
          <motion.div key={s.l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 1.3 + i * 0.08 }}
            className={`py-7 ${i % 2 ? "pl-5 sm:pl-8" : "pr-5"} ${i > 0 ? "lg:border-l lg:border-white/10 lg:pl-8" : ""} ${i % 2 ? "border-l border-white/10" : ""} ${i > 1 ? "border-t border-white/10 lg:border-t-0" : ""}`}
            data-testid={`hero-stat-${i}`}>
            <div className="font-display text-4xl font-bold tracking-tight text-[#CCFF00] sm:text-5xl">{s.v}</div>
            <div className="mt-1 text-sm text-slate-300">{s.l}</div>
          </motion.div>
        ))}
      </Container>
    </div>
  </section>
);
